import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import { useToast } from 'use-toast-mui';
import { searchTicketShareCandidates, updateTicketShares } from '../../../../api/ticket';
import { errorMessages } from '../../../../utils/methods';
import {
  ModalContent,
  FormSection,
  FormRow,
  ButtonWrapper,
  CancelButton,
  SaveButton,
  PeriodFormLabel,
} from '../../ChangeTFreezyModalStyled';

const USER_TYPE_TEMPORARY = 'T';
// 공유 추가는 사용 중 수강권만(서버도 같은 규칙, 중지 포함 그 외 상태에선 해제만 할 수 있다).
const ADDABLE_STATUS = 'ACTIVE';
// 검색 조건(서버와 같은 규칙) — 전화번호 전체(2026-10-08) 또는 앱 아이디 전체(2026-10-29)가 일치할 때만.
// 타 지점 회원 노출을 막으려고 이름 검색은 없다. 숫자뿐이면 번호 입력 중으로 보고 아이디로 찾지 않는다.
const PHONE_KEYWORD = /^01\d{8,9}$/;
const USERNAME_KEYWORD = /^(?=.*[A-Za-z])\S{4,50}$/;
const SEARCH_DEBOUNCE_MS = 300;

/** 검색어 → 서버로 보낼 값. 조건 미달이면 null. */
const toKeyword = input => {
  const trimmed = input.trim();
  const digits = trimmed.replace(/[\s-]/g, '');
  if (PHONE_KEYWORD.test(digits)) {
    return digits;
  }
  return USERNAME_KEYWORD.test(trimmed) ? trimmed : null;
};

/**
 * 상품 정보 수정 > 공유 탭(#154). 전화번호나 앱 아이디로 회원을 찾아 이 수강권을 함께 쓸 회원을 고른다.
 * 잔여 횟수는 같이 쓰고, 일일·주간·취소 제한은 회원마다 따로 적용된다.
 * 잠재회원을 고르면 저장할 때 정회원으로 전환된다. 저장은 목록 전체 치환이다.
 */
export default function TicketShareTab({ bigUser, ticket, user, onDone }) {
  const toast = useToast();
  // key = 'M:회원ID' | 'T:잠재회원ID'
  const savedShares = () =>
    (ticket?.sharedMembers ?? []).map(m => ({
      key: `M:${m.memberId}`,
      userType: 'M',
      id: m.memberId,
      name: m.name,
      phoneNumber: m.phoneNumber,
    }));
  const [selected, setSelected] = useState(savedShares);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const latestRequest = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canAdd = ticket?.status === ADDABLE_STATUS;

  const add = candidate => {
    const key = `${candidate.userType}:${candidate.id}`;
    setSelected(prev => (prev.some(s => s.key === key) ? prev : [...prev, { ...candidate, key }]));
    setCandidates(null);
    setQuery('');
  };

  const remove = key => setSelected(prev => prev.filter(s => s.key !== key));

  // 입력이 멈추면 미리보기 — 검색 버튼 없음. 늦게 온 이전 응답은 버린다.
  const keyword = toKeyword(query);
  useEffect(() => {
    if (!keyword) {
      latestRequest.current += 1; // 진행 중인 이전 응답이 목록을 다시 띄우지 않게
      setCandidates(null);
      return undefined;
    }
    const requestId = ++latestRequest.current;
    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const result = await searchTicketShareCandidates(
          user.memberId,
          ticket.memberCourseTicketId,
          keyword,
          bigUser.token,
        );
        if (requestId === latestRequest.current) setCandidates(result ?? []);
      } catch (error) {
        if (requestId === latestRequest.current) toast.error(errorMessages(error));
      } finally {
        if (requestId === latestRequest.current) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCandidates = (candidates ?? []).filter(c => !selected.some(s => s.key === `${c.userType}:${c.id}`));

  // 해제해도 이 지점 회원목록에 만료로 남는다(#172) — 해제 안내창 없이 바로 저장.
  const submit = async () => {
    try {
      setIsSubmitting(true);
      const data = {
        memberIds: selected.filter(s => s.userType !== USER_TYPE_TEMPORARY).map(s => s.id),
        temporaryUserIds: selected.filter(s => s.userType === USER_TYPE_TEMPORARY).map(s => Number(s.id)),
      };
      await updateTicketShares(user.memberId, ticket.memberCourseTicketId, data, bigUser.token);
      toast.success('공유 설정이 저장되었습니다');
      await onDone?.();
    } catch (error) {
      toast.error(errorMessages(error));
      // 저장 실패(예: 예정 예약이 있어 해제 불가)면 서버 상태 그대로인 저장된 목록으로 되돌린다.
      setSelected(savedShares());
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContent>
      <FormSection>
        {canAdd ? (
          // 안내 줄이 아래에 붙어 있어 가운데 정렬이면 라벨이 입력칸보다 내려간다 — 위에 붙이고 입력칸 높이(44px)에 맞춘다
          <FormRow sx={{ alignItems: 'flex-start' }}>
            <PeriodFormLabel sx={{ lineHeight: '44px' }}>회원 검색</PeriodFormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField
                size="small"
                value={query}
                placeholder="전화번호 전체 또는 앱 아이디"
                onChange={e => setQuery(e.target.value)}
                sx={{ width: 280, '& .MuiOutlinedInput-root': { height: '44px', borderRadius: '8px' } }}
              />
              <Typography sx={{ fontSize: 12, color: '#9E9E9E' }}>
                전화번호 또는 앱 아이디를 끝까지 입력하세요.
              </Typography>
            </Box>
          </FormRow>
        ) : (
          <Typography sx={{ fontSize: 13, color: '#EE735A' }}>
            사용 중인 수강권만 공유 회원을 추가할 수 있습니다. 기존 공유 해제만 가능합니다.
          </Typography>
        )}

        {keyword && candidates && (
          <Box sx={{ border: '1px solid #ECEFF1', borderRadius: '8px', maxHeight: 260, overflowY: 'auto' }}>
            <Typography sx={{ fontSize: 12, color: '#757575', px: 2, pt: 1.5, pb: 0.5 }}>
              {visibleCandidates.length === 0
                ? isSearching
                  ? '검색 중…'
                  : '검색된 회원이 없습니다.'
                : '공유할 회원을 선택하세요. 결과가 많으면 더 입력해 범위를 좁혀 주세요.'}
            </Typography>
            {visibleCandidates.map(c => (
              <Box
                key={`${c.userType}:${c.id}`}
                onClick={() => add(c)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  fontSize: 14,
                  '&:hover': { background: '#F2F8F8' },
                }}>
                <Chip
                  size="small"
                  label={c.userType === USER_TYPE_TEMPORARY ? '잠재회원' : '정회원'}
                  sx={{
                    height: 22,
                    fontSize: 12,
                    color: c.userType === USER_TYPE_TEMPORARY ? '#EE735A' : '#0F7F85',
                    background: c.userType === USER_TYPE_TEMPORARY ? '#FDEEEA' : '#E3F2F3',
                  }}
                />
                <Box sx={{ fontWeight: 600 }}>{c.name}</Box>
                <Box sx={{ color: '#555' }}>{c.phoneNumber}</Box>
                {c.birthDate && <Box sx={{ color: '#9E9E9E' }}>{`${String(c.birthDate).slice(0, 4)}년생`}</Box>}
                {c.branchName && <Box sx={{ color: '#9E9E9E', ml: 'auto' }}>{c.branchName}</Box>}
              </Box>
            ))}
          </Box>
        )}

        <FormRow sx={{ alignItems: 'flex-start' }}>
          <PeriodFormLabel>공유 회원</PeriodFormLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
            {selected.length === 0 && (
              <Typography sx={{ fontSize: 14, color: '#9E9E9E', lineHeight: '32px' }}>
                공유 중인 회원이 없습니다
              </Typography>
            )}
            {selected.map(s => (
              <Chip
                key={s.key}
                label={`${s.name}${s.phoneNumber ? ` (${s.phoneNumber})` : ''}${s.userType === USER_TYPE_TEMPORARY ? ' · 잠재회원' : ''}`}
                onDelete={() => remove(s.key)}
                sx={{ fontSize: 13 }}
              />
            ))}
          </Box>
        </FormRow>

        <Typography sx={{ fontSize: 12, color: '#757575', lineHeight: 1.6 }}>
          잔여 횟수는 공유 회원이 함께 사용하고, 일일·주간·취소 제한은 회원마다 따로 적용됩니다.
          <br />
          잠재회원은 전화번호로만 찾을 수 있고, 저장 시 정회원으로 전환됩니다.
          <br />
          공유를 해제하려면 해당 회원의 예정 예약을 먼저 취소해야 합니다.
          <br />
          해제한 회원은 우리 지점 회원목록에 만료로 남습니다.
        </Typography>
      </FormSection>

      <ButtonWrapper>
        <CancelButton onClick={() => onDone?.()}>취소</CancelButton>
        <SaveButton isActive={!isSubmitting} disabled={isSubmitting} onClick={submit}>
          저장
        </SaveButton>
      </ButtonWrapper>

    </ModalContent>
  );
}
